/**
 * admin/page.tsx — Admin Center
 * Imports all sub-tabs from the components/ directory.
 * Each tab is a separate component file for maintainability.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import MakeAPageTab from "./MakeAPageTab";
import PopupsBannersTab from "./PopupsBannersTab";
import SiteMetaSettings from "@/components/feature/SiteMetaSettings";
import {
  FormEntriesTab,
  LogoEditor,
  LogoFontPicker,
  ImageLibraryPanel,
  SiteDirectoryTab,
  SupabaseDeployStatus,
  PasswordChangePanel,
} from "./components";

type Tab = "dashboard" | "entries" | "settings" | "directory" | "pages" | "notifications";

export default function AdminPage() {
  const { isAdmin, login, logout } = useAdmin();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("dashboard");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    setTimeout(() => {
      const ok = login(password);
      if (ok) { navigate("/"); }
      else { setError(true); setPassword(""); }
      setLoading(false);
    }, 400);
  };

  const handleLogout = () => { logout(); navigate("/"); };

  const bg = isDark ? "bg-gray-950" : "bg-gray-50";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const headingColor = isDark ? "text-white" : "text-gray-800";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const tabActive = "bg-pink-500 text-white";
  const tabInactive = isDark ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-500 hover:text-gray-800 hover:bg-gray-100";

  if (!isAdmin) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 transition-colors duration-300 ${isDark ? "bg-gray-950" : "bg-white"}`}>
        <div className="w-full max-w-sm">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} className="flex items-center gap-1.5 text-gray-300 hover:text-gray-500 text-xs mb-10 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i> Back to site
          </a>
          <div className="w-12 h-12 flex items-center justify-center bg-pink-50 rounded-full mx-auto mb-6">
            <i className="ri-lock-line text-pink-400 text-xl"></i>
          </div>
          <h1 className={`text-2xl font-bold mb-1 text-center ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
            Admin Access
          </h1>
          <p className={`text-sm text-center mb-8 ${subColor}`}>Enter your password to enable editing.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(false); }}
                placeholder="Password"
                autoFocus
                className={`w-full border rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 transition-all ${error ? "border-red-300 focus:ring-red-200" : "border-gray-200 focus:ring-pink-200"}`}
              />
              {error && <p className="text-red-400 text-xs mt-1.5 pl-1">Incorrect password. Try again.</p>}
            </div>
            <button
              type="submit"
              disabled={loading || !password}
              className="w-full bg-gray-900 hover:bg-gray-700 disabled:opacity-40 text-white font-semibold py-3 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? <i className="ri-loader-4-line animate-spin"></i> : "Unlock"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${bg}`}>
      {/* Top bar */}
      <div className={`sticky top-0 z-40 border-b ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-pink-500">
              <i className="ri-settings-3-line text-white text-sm"></i>
            </div>
            <div>
              <span className={`font-bold text-base ${headingColor}`}>
                Admin Center
              </span>
              <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? "bg-green-900/40 text-green-400" : "bg-green-50 text-green-600"}`}>Settings</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-1.5 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-full"
            >
              <i className="ri-edit-line"></i>
              Edit Site
            </button>
            <button
              onClick={handleLogout}
              className={`flex items-center gap-1.5 text-sm cursor-pointer whitespace-nowrap transition-colors ${isDark ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
            >
              <i className="ri-logout-box-r-line"></i>
              Log out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-5xl mx-auto px-6 flex gap-1 pb-0">
          {([
            { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line" },
            { id: "directory", label: "Site Directory", icon: "ri-pages-line" },
            { id: "pages", label: "Make a Page", icon: "ri-file-add-line" },
            { id: "notifications", label: "Popups & Banners", icon: "ri-megaphone-line" },
            { id: "entries", label: "Form Entries", icon: "ri-table-line" },
            { id: "settings", label: "Settings", icon: "ri-settings-3-line" },
          ] as { id: Tab; label: string; icon: string }[]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-t-lg cursor-pointer whitespace-nowrap transition-colors ${activeTab === tab.id ? tabActive : tabInactive}`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            <div>
              <h2 className={`text-xl font-bold mb-1 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
                Welcome back!
              </h2>
              <p className={`text-sm ${subColor}`}>Manage your Makers Alley Co website from here.</p>
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: "ri-edit-line", label: "Edit Website", sub: "Update content, text, images", color: "bg-pink-50 text-pink-500", action: () => navigate("/") },
                { icon: "ri-gallery-line", label: "Image Gallery", sub: "Upload & manage all photos", color: "bg-pink-50 text-pink-500", action: () => navigate("/admin/gallery") },
                { icon: "ri-briefcase-line", label: "Careers Page", sub: "Manage job listings", color: "bg-green-50 text-green-500", action: () => navigate("/careers") },
                { icon: "ri-palette-line", label: "Artists Page", sub: "Manage artist profiles", color: "bg-amber-50 text-amber-500", action: () => navigate("/artists") },
                { icon: "ri-file-add-line", label: "Make a Page", sub: "Create custom pages", color: "bg-pink-50 text-pink-500", action: () => setActiveTab("pages") },
                { icon: "ri-megaphone-line", label: "Popups & Banners", sub: "Announcements & modals", color: "bg-amber-50 text-amber-500", action: () => setActiveTab("notifications") },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={item.action}
                  className={`${cardBg} border rounded-2xl p-5 text-left cursor-pointer hover:border-pink-300 transition-all`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${item.color}`}>
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <div className={`font-bold text-sm mb-0.5 ${headingColor}`}>{item.label}</div>
                  <div className={`text-xs ${subColor}`}>{item.sub}</div>
                </button>
              ))}
            </div>

            {/* Admin features overview */}
            <div className={`rounded-2xl border p-5 ${cardBg}`}>
              <h3 className={`font-bold text-sm mb-3 ${headingColor}`}>
                <i className="ri-tools-line text-pink-400 mr-1.5"></i>
                Admin Features on the Homepage
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: "ri-arrow-up-down-line", label: "Arrow Buttons to Reorder", desc: "Hover any section and use the up/down arrows to reorder" },
                  { icon: "ri-eye-off-line", label: "Hide Sections", desc: "Toggle visibility per section — hidden from visitors" },
                  { icon: "ri-eye-line", label: "Preview as Visitor", desc: "See exactly what visitors see, no admin chrome" },
                  { icon: "ri-font-size", label: "Logo Font", desc: "Change the logo & heading font in Settings" },
                ].map((f) => (
                  <div key={f.label} className={`flex items-start gap-3 p-3 rounded-xl ${isDark ? "bg-gray-700/50" : "bg-gray-50"}`}>
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-pink-100 flex-shrink-0">
                      <i className={`${f.icon} text-pink-500 text-sm`}></i>
                    </div>
                    <div>
                      <div className={`text-xs font-bold ${headingColor}`}>{f.label}</div>
                      <div className={`text-xs ${subColor}`}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`rounded-2xl border p-5 ${cardBg}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`font-bold text-sm ${headingColor}`}>
                  <i className="ri-table-line text-pink-400 mr-1.5"></i>
                  Form Entries
                </h3>
                <button
                  onClick={() => setActiveTab("entries")}
                  className="text-xs text-pink-500 hover:text-pink-700 font-semibold cursor-pointer"
                >
                  View all &rarr;
                </button>
              </div>
              <p className={`text-xs ${subColor}`}>
                Click "View all" to see all form submissions from your website visitors.
              </p>
            </div>
          </div>
        )}

        {activeTab === "directory" && <SiteDirectoryTab isDark={isDark} />}
        {activeTab === "pages" && <MakeAPageTab isDark={isDark} />}
        {activeTab === "notifications" && <PopupsBannersTab isDark={isDark} />}
        {activeTab === "entries" && <FormEntriesTab isDark={isDark} />}

        {activeTab === "settings" && (
          <div className="space-y-6">
            <div>
              <h2 className={`text-xl font-bold mb-1 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
                Settings
              </h2>
              <p className={`text-sm ${subColor}`}>Manage your admin preferences and site appearance.</p>
            </div>

            {/* Site meta: OG image, favicon, site name */}
            <SiteMetaSettings />

            {/* Logo design editor */}
            <LogoEditor isDark={isDark} />

            {/* Logo font picker */}
            <LogoFontPicker isDark={isDark} />

            {/* Image library */}
            <ImageLibraryPanel isDark={isDark} />

            {/* Password management */}
            <PasswordChangePanel isDark={isDark} />

            {/* Supabase sync deploy status */}
            <SupabaseDeployStatus isDark={isDark} />
          </div>
        )}
      </div>
    </div>
  );
}